/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useSelector } from "react-redux";

import { LinkEmbedType } from "@vienna/common";

import { LinkBlock } from "src/classes/block/block-types";
import { InstagramEmbed } from "src/components/Block/BlockTypes/LinkBlock/Instagram/InstagramEmbed";
import { SpotifyEmbed } from "src/components/Block/BlockTypes/LinkBlock/Spotify/SpotifyEmbed";
import { StyledLinkBlockContent } from "src/components/Block/BlockTypes/LinkBlock/styles";
import { TwitterEmbed } from "src/components/Block/BlockTypes/LinkBlock/Twitter/TwitterEmbed";
import { YoutubeEmbed } from "src/components/Block/BlockTypes/LinkBlock/Youtube/YouTubeEmbed";
import { ErrorBlock } from "src/components/Block/Placeholders/ErrorBlock";
import type { AppState } from "src/store";

interface Props {
  block: LinkBlock;
}

export function SLinkBlockContent({ block }: Props) {
  const theme = useSelector((state: AppState) => state.globalUI.uiSettings.theme);
  const linkEmbedType = block.snapshot.linkEmbedType;
  const url = block.snapshot.url;
  return (
    <StyledLinkBlockContent>
      {(() => {
        switch (linkEmbedType) {
          case LinkEmbedType.Twitter:
            if (!block.getTweetId) return <ErrorBlock>Link Embed Failed to Render link ${url}</ErrorBlock>;
            return <TwitterEmbed tweetId={block.getTweetId!} theme={theme} disableMouse={false} />;
          case LinkEmbedType.Youtube:
            if (!block.getYoutubeVideoId) return <ErrorBlock>Link Embed Failed to Render link ${url}</ErrorBlock>;
            return <YoutubeEmbed youtubeId={block.getYoutubeVideoId} disableMouse={false} />;
          case LinkEmbedType.Spotify:
            if (!block.getSpotifyEmbedId) return <ErrorBlock>Link Embed Failed to Render link ${url}</ErrorBlock>;
            return (
              <SpotifyEmbed embedId={block.getSpotifyEmbedId} blockId={block.snapshot.id} disableMouse={false} />
            );
          case LinkEmbedType.Instagram:
            if (!block.getInstagramEmbedId)
              return <ErrorBlock>Link Embed Failed to Render link ${url}</ErrorBlock>;
            return <InstagramEmbed embedId={block.getInstagramEmbedId} disableMouse={false} />;
          case LinkEmbedType.Other:
            console.error("Hit 'other' in link block switch statement. This is not implemented yet.");
            return <ErrorBlock>Link Embed Failed to Render link ${url}</ErrorBlock>;
        }
      })()}
    </StyledLinkBlockContent>
  );
}
